/**
 * AWS RDS compliance configuration.
 *
 * Lifecycle data sourced from:
 *   https://docs.aws.amazon.com/AmazonRDS/latest/PostgreSQLReleaseNotes/postgresql-release-calendar.html
 *
 * To add MySQL/Aurora lifecycle data, add a similar table and extend
 * evaluateCompliance() to handle the 'mysql' / 'aurora-mysql' engine tags.
 */

// ---------------------------------------------------------------------------
// PostgreSQL RDS lifecycle dates
// ---------------------------------------------------------------------------

export interface PostgresLifecycleEntry {
  majorVersion: number;
  /** RDS end of standard support (YYYY-MM-DD) */
  standardSupportEnd: string;
  /** RDS end of extended support (YYYY-MM-DD), null if deprecated / N/A */
  extendedSupportEnd: string | null;
}

export const POSTGRES_LIFECYCLE: PostgresLifecycleEntry[] = [
  {
    majorVersion: 18,
    standardSupportEnd: '2031-02-28',
    extendedSupportEnd: '2034-02-28',
  },
  {
    majorVersion: 17,
    standardSupportEnd: '2030-02-28',
    extendedSupportEnd: '2033-02-28',
  },
  {
    majorVersion: 16,
    standardSupportEnd: '2029-02-28',
    extendedSupportEnd: '2032-02-29',
  },
  {
    majorVersion: 15,
    standardSupportEnd: '2028-02-29',
    extendedSupportEnd: '2031-02-28',
  },
  {
    majorVersion: 14,
    standardSupportEnd: '2027-02-28',
    extendedSupportEnd: '2030-02-28',
  },
  {
    majorVersion: 13,
    standardSupportEnd: '2026-02-28',
    extendedSupportEnd: '2029-02-28',
  },
  {
    majorVersion: 12,
    standardSupportEnd: '2025-02-28',
    extendedSupportEnd: '2028-02-29',
  },
  {
    majorVersion: 11,
    standardSupportEnd: '2024-02-29',
    extendedSupportEnd: '2027-03-31',
  },
  {
    majorVersion: 10,
    standardSupportEnd: '2023-04-30',
    extendedSupportEnd: null,
  },
  {
    majorVersion: 9,
    standardSupportEnd: '2022-04-30',
    extendedSupportEnd: null,
  },
];

const postgresLookup = new Map(
  POSTGRES_LIFECYCLE.map(e => [e.majorVersion, e]),
);

// ---------------------------------------------------------------------------
// Instance class eligibility for Database Savings Plans
// ---------------------------------------------------------------------------

/**
 * Eligible instance families:
 *   - db.t*          (burstable, any generation)
 *   - db.[rmc][78]g* (R/M/C family, generation 7 or 8, Graviton)
 *   - db.serverless
 */
const SAVINGS_PLAN_ELIGIBLE = /^db\.(t\d|[rmc][78]g|serverless)/;

// ---------------------------------------------------------------------------
// Compliance issue types
// ---------------------------------------------------------------------------

export type ComplianceSeverity = 'warning' | 'critical';
export type ComplianceField = 'engine' | 'instanceClass';

export interface ComplianceIssue {
  severity: ComplianceSeverity;
  field: ComplianceField;
  message: string;
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

function parsePostgresMajorVersion(engineVersion: string): number | null {
  const match = engineVersion.match(/^(\d+)\./);
  if (!match) return null;
  const v = parseInt(match[1], 10);
  // PostgreSQL major versions are >= 9; anything like 8.x.mysql_aurora is MySQL
  return v >= 9 ? v : null;
}

export function evaluateCompliance(
  engineVersion: string,
  instanceClass: string,
  tags: string[],
  specType: string,
  now: Date = new Date(),
): ComplianceIssue[] {
  const issues: ComplianceIssue[] = [];

  const isPostgres = tags.some(
    t => t === 'postgresql' || t === 'aurora-postgresql',
  );

  // --- Engine lifecycle check (PostgreSQL only for now) ---
  if (isPostgres && engineVersion) {
    const major = parsePostgresMajorVersion(engineVersion);
    const entry = major !== null ? postgresLookup.get(major) : undefined;

    if (entry) {
      const standardEnd = new Date(entry.standardSupportEnd);
      const sixMonthsBefore = new Date(standardEnd);
      sixMonthsBefore.setMonth(sixMonthsBefore.getMonth() - 6);

      if (now > standardEnd) {
        issues.push({
          severity: 'warning',
          field: 'engine',
          message: `This instance is nearing the end of standard support on ${entry.standardSupportEnd}`,
        });
      } else if (now >= sixMonthsBefore) {
        issues.push({
          severity: 'warning',
          field: 'engine',
          message: `This instance is nearing the end of standard support on ${entry.standardSupportEnd}`,
        });
      }

      if (entry.extendedSupportEnd) {
        const extendedEnd = new Date(entry.extendedSupportEnd);
        const oneYearBeforeExtended = new Date(extendedEnd);
        oneYearBeforeExtended.setFullYear(
          oneYearBeforeExtended.getFullYear() - 1,
        );

        if (now >= oneYearBeforeExtended) {
          issues.push({
            severity: 'critical',
            field: 'engine',
            message: `This instance is nearing the end of extended support on ${entry.extendedSupportEnd}`,
          });
        }
      } else if (now > standardEnd) {
        issues.push({
          severity: 'critical',
          field: 'engine',
          message:
            'This engine version is deprecated with no extended support available',
        });
      }
    }
  }

  // --- Instance class check (instances only, not clusters) ---
  if (specType === 'rds-instance' && instanceClass) {
    if (!SAVINGS_PLAN_ELIGIBLE.test(instanceClass)) {
      issues.push({
        severity: 'warning',
        field: 'instanceClass',
        message:
          'This instance cannot be used for database savings plans until its instance class is upgraded',
      });
    }
  }

  return issues;
}

/** Return the worst severity across a list of issues, or null if clean. */
export function worstSeverity(
  issues: ComplianceIssue[],
): ComplianceSeverity | null {
  if (issues.some(i => i.severity === 'critical')) return 'critical';
  if (issues.some(i => i.severity === 'warning')) return 'warning';
  return null;
}

/** Filter issues to those targeting a specific field. */
export function issuesForField(
  issues: ComplianceIssue[],
  field: ComplianceField,
): ComplianceIssue[] {
  return issues.filter(i => i.field === field);
}
